// La bitácora en palabras · DIRECTRIZ-EXPERIENCIA E3
//
// Medido el 16 de septiembre de 2026: la lista de Auditoría ya era una
// tabla, pero enseñaba el código de la acción (`LOGIN_EXITOSO`), la
// tabla y el id interno (`usuarios #30`), la IP cruda y el `user_agent`
// entero. Aquí viven las traducciones. Todo puro y sin React, para
// poder probarlo; y con caída al código cuando falta una palabra —
// nunca se oculta lo que pasó.

import { etiquetaCampo } from './etiquetas.js';

// Los códigos que no se dejan leer solos (en inglés, o sin verbo).
export const ACCIONES = {
  LOGIN_EXITOSO: 'Entró a TEGO',
  LOGIN_FALLIDO: 'Intentó entrar y no pudo',
  LOGIN_BLOQUEADO: 'Intentó entrar con la cuenta bloqueada',
  SESION_CERRADA: 'Cerró sesión',
  SESION_RENOVADA: 'Siguió trabajando (sesión renovada)',
  PASSWORD_CAMBIADA: 'Cambió su contraseña',
  BOOTSTRAP_DUENO: 'Se creó la cuenta del dueño',
  REGISTRO_ORGANIZACION: 'Se registró la empresa',
  BOT_PREGUNTA: 'Preguntó al asistente',
  BOT_ACCION: 'Hizo algo desde el asistente',
  MOVIMIENTO_ENTRADA: 'Registró una entrada',
  MOVIMIENTO_SALIDA: 'Registró una salida',
  MOVIMIENTO_AJUSTE: 'Ajustó una existencia',
  MOVIMIENTO_TRASPASO: 'Registró un traspaso',
  MOVIMIENTO_VENTA: 'Registró una venta',
  MOVIMIENTO_MERMA: 'Registró una merma',
  MOVIMIENTO_DEVOLUCION: 'Registró una devolución',
  VENTA_REGISTRADA: 'Cobró una venta',
  TOTP_ACTIVADO: 'Activó su segundo factor',
  TOTP_DESACTIVADO: 'Desactivó su segundo factor',
  TERMINAL_CONECTADA: 'Conectó una terminal',
  TERMINAL_ENROLADA: 'Dio de alta una terminal',
  TERMINAL_AJUSTADA: 'Ajustó desde la terminal',
  SESION_TERMINAL_ABIERTA: 'Abrió turno en una terminal',
  SESION_TERMINAL_CERRADA: 'Cerró turno en una terminal',
  CREDENCIALES_APARATO_CONSULTADAS: 'Consultó las credenciales de un aparato',
  CREDENCIAL_BROKER_CONSULTADA: 'Consultó la credencial de conexión',
};

// La tabla, en la palabra que usa la gente. Lo que no está, se deriva.
export const ENTIDADES = {
  usuarios: 'Persona', usuario: 'Persona', productos: 'Producto', producto: 'Producto',
  movimientos_inventario: 'Movimiento', movimiento: 'Movimiento', ubicaciones: 'Ubicación', ubicacion: 'Ubicación',
  sesion: 'Sesión', sesion_terminal: 'Turno de terminal', dispositivo: 'Dispositivo', dispositivos: 'Dispositivo',
  dispositivo_credencial: 'Credencial de aparato', mqtt_credencial: 'Credencial de conexión',
  gafete: 'Gafete', firmware_terminal: 'Firmware de terminal', balda_bascula: 'Balda', lectura_balda: 'Lectura de balda',
  movimiento_en_cuarentena: 'Movimiento en cuarentena', archivo_calidad: 'Archivo de calidad', adjunto_calidad: 'Archivo de calidad',
  producto_imagenes: 'Foto de producto', pago: 'Pago', venta: 'Venta', turno_caja: 'Turno de caja',
  corporaciones: 'Empresa', corporacion: 'Empresa', andon_regla: 'Regla del andón', bot_mensaje: 'Pregunta al asistente',
  orden_compra: 'Orden de compra', recepcion: 'Recepción', traslado: 'Traslado', conteo: 'Conteo', lote: 'Lote', lotes: 'Lote',
  cliente: 'Cliente', proveedor: 'Proveedor', factura: 'Factura', cargo_proveedor: 'Cargo a proveedor',
  inteligencia_ubicacion_sugerida: 'Ubicación sugerida', asistente_pregunta: 'Pregunta al asistente operativo',
};

function frase(codigo) {
  const palabras = String(codigo || '').toLowerCase().split('_').filter(Boolean);
  if (!palabras.length) return '—';
  const texto = palabras.join(' ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** `PRODUCTO_CREADO` → «Producto creado»; `LOGIN_EXITOSO` → «Entró a TEGO». */
export function describirAccion(codigo) {
  return ACCIONES[codigo] || frase(codigo);
}

/** La entidad en humano, SIN id interno: «Producto», «Persona». */
export function describirEntidad(entidad) {
  if (!entidad) return '—';
  if (ENTIDADES[entidad]) return ENTIDADES[entidad];
  return frase(entidad.replace(/s$/, ''));
}

/** Lo que mejor identifica el registro para una persona: código, folio,
 *  nombre o correo, leído de los datos del cambio. Nunca el id. */
export function etiquetaDe(r) {
  const datos = { ...(r.datos_antes || {}), ...(r.datos_despues || {}) };
  for (const k of ['sku', 'folio', 'codigo', 'nombre', 'nombre_completo', 'email', 'identificador', 'codigo_lote']) {
    const v = datos[k];
    if (typeof v === 'string' && v.trim()) return v.trim().slice(0, 60);
    if (typeof v === 'number') return String(v);
  }
  return null;
}

export function describirSobreQue(r) {
  const e = describirEntidad(r.entidad);
  const et = etiquetaDe(r);
  return et ? `${e} · ${et}` : e;
}

/** «Red local» / «Internet» — la IP exacta queda en el detalle técnico. */
export function describirDesde(ip) {
  if (!ip) return '—';
  if (/^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|127\.|::1$|fc|fd)/.test(ip)) return 'Red local';
  return 'Internet';
}

/** «Chrome en Windows», «Safari en iPhone», «Terminal de conteo». */
export function describirDispositivo(ua) {
  if (!ua) return '—';
  if (/tego-terminal|TerminalTEGO|python-requests|curl/i.test(ua)) return 'Terminal o aparato';
  let nav = 'Navegador';
  if (/Edg\//.test(ua)) nav = 'Edge';
  else if (/OPR\//.test(ua)) nav = 'Opera';
  else if (/Chrome\//.test(ua)) nav = 'Chrome';
  else if (/Firefox\//.test(ua)) nav = 'Firefox';
  else if (/Safari\//.test(ua)) nav = 'Safari';
  let so = '';
  if (/Android/.test(ua)) so = 'Android';
  else if (/iPhone|iPad/.test(ua)) so = 'iPhone o iPad';
  else if (/Windows/.test(ua)) so = 'Windows';
  else if (/Mac OS/.test(ua)) so = 'Mac';
  else if (/Linux/.test(ua)) so = 'Linux';
  return so ? `${nav} en ${so}` : nav;
}

// El nombre del campo, en la palabra de la gente. La tabla vive en
// `etiquetas.js` (una sola fuente para toda pantalla); aquí solo se usa.
export function describirCampo(campo) {
  return etiquetaCampo(campo);
}

/** Un valor, siempre legible: un objeto se abre campo a campo, nunca JSON. */
export function aplanar(valor, prefijo = '') {
  if (valor === null || valor === undefined || valor === '') return [[prefijo || '—', '—']];
  if (Array.isArray(valor)) {
    if (!valor.length) return [[prefijo, 'ninguno']];
    return valor.flatMap((v, i) => aplanar(v, prefijo ? `${prefijo} ${i + 1}` : String(i + 1)));
  }
  if (typeof valor === 'object') {
    return Object.entries(valor).flatMap(([k, v]) => aplanar(v, prefijo ? `${prefijo} · ${describirCampo(k)}` : describirCampo(k)));
  }
  if (typeof valor === 'boolean') return [[prefijo, valor ? 'Sí' : 'No']];
  return [[prefijo, String(valor)]];
}
